import React, { useEffect, useState } from "react";
import { FaBolt, FaHome, FaThList, FaLayerGroup, FaClock, FaShoppingCart } from "react-icons/fa";
import { MdTimeline, MdCategory } from "react-icons/md";
import { toast } from "react-toastify";
import {
  getControlCentreSettings,
  toggleFlashDiscountSingleProductMode,
  toggleFlashDiscountShowOnHome,
  toggleTimerDealShowOnHome,
  toggleHideProductDeliveryTimeline,
  toggleHideStickyCategoryRow,
  toggleShowSubCategoriesInStickyCategory,
  toggleShowSubCategoriesInMainCategory,
  toggleHideMainCategory,
  toggleGlobalCartLimit,
  updateGlobalMinimumCartValue,
} from "../../services/ClientService";

const ControlCentre = () => {
  const [flashSingleProductMode, setFlashSingleProductMode] = useState(false);
  const [flashShowOnHome, setFlashShowOnHome] = useState(false);
  const [timerShowOnHome, setTimerShowOnHome] = useState(false);
  const [hideProductDeliveryTimeline, setHideProductDeliveryTimeline] = useState(false);
  const [hideStickyCategoryRow, setHideStickyCategoryRow] = useState(false);
  const [showSubCategoriesInStickyCategory, setShowSubCategoriesInStickyCategory] = useState(false);
  const [showSubCategoriesInMainCategory, setShowSubCategoriesInMainCategory] = useState(false);
  const [hideMainCategory, setHideMainCategory] = useState(false);
  const [globalCartLimitEnabled, setGlobalCartLimitEnabled] = useState(false);
  const [globalMinimumCartValue, setGlobalMinimumCartValue] = useState("");
  const [savedMinimumCartValue, setSavedMinimumCartValue] = useState(0);
  const [flashToggleLoading, setFlashToggleLoading] = useState(false);
  const [flashHomeToggleLoading, setFlashHomeToggleLoading] = useState(false);
  const [timerHomeToggleLoading, setTimerHomeToggleLoading] = useState(false);
  const [timelineToggleLoading, setTimelineToggleLoading] = useState(false);
  const [stickyRowToggleLoading, setStickyRowToggleLoading] = useState(false);
  const [stickySubToggleLoading, setStickySubToggleLoading] = useState(false);
  const [mainSubToggleLoading, setMainSubToggleLoading] = useState(false);
  const [hideMainCategoryToggleLoading, setHideMainCategoryToggleLoading] = useState(false);
  const [globalCartLimitToggleLoading, setGlobalCartLimitToggleLoading] = useState(false);
  const [globalCartSaveLoading, setGlobalCartSaveLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getControlCentreSettings();
        if (res?.success && res.data) {
          setFlashSingleProductMode(!!res.data.singleProductMode);
          setFlashShowOnHome(!!res.data.showOnHome);
          setTimerShowOnHome(!!res.data.timerDealShowOnHome);
          setHideProductDeliveryTimeline(!!res.data.hideProductDeliveryTimeline);
          setHideStickyCategoryRow(!!res.data.hideStickyCategoryRow);
          setShowSubCategoriesInStickyCategory(!!res.data.showSubCategoriesInStickyCategory);
          setShowSubCategoriesInMainCategory(!!res.data.showSubCategoriesInMainCategory);
          setHideMainCategory(!!res.data.hideMainCategory);
          setGlobalCartLimitEnabled(!!res.data.globalCartLimitEnabled);
          const minVal = Math.max(0, Number(res.data.globalMinimumCartValue) || 0);
          setSavedMinimumCartValue(minVal);
          setGlobalMinimumCartValue(minVal > 0 ? String(minVal) : "");
        }
      } catch (err) {
        toast?.error?.("Could not load Control Centre settings");
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, []);

  const handleToggleFlashSingleProductMode = async () => {
    if (flashToggleLoading) return;
    const nextValue = !flashSingleProductMode;
    setFlashToggleLoading(true);
    setFlashSingleProductMode(nextValue);
    try {
      const res = await toggleFlashDiscountSingleProductMode({ singleProductMode: nextValue });
      if (res?.success) {
        toast?.success?.(
          nextValue
            ? "Single-product Flash Discount mode enabled"
            : "Single-product Flash Discount mode disabled"
        );
      } else {
        setFlashSingleProductMode(!nextValue);
        toast?.error?.(res?.message || "Failed to update Flash Discount setting");
      }
    } catch (err) {
      setFlashSingleProductMode(!nextValue);
      toast?.error?.("Failed to update Flash Discount setting");
    } finally {
      setFlashToggleLoading(false);
    }
  };

  const handleToggleTimerShowOnHome = async () => {
    if (timerHomeToggleLoading) return;
    const nextValue = !timerShowOnHome;
    setTimerHomeToggleLoading(true);
    setTimerShowOnHome(nextValue);
    try {
      const res = await toggleTimerDealShowOnHome({ showOnHome: nextValue });
      if (res?.success) {
        toast?.success?.(nextValue ? "Timer Deals will appear on Home" : "Timer Deals removed from Home");
      } else {
        setTimerShowOnHome(!nextValue);
        toast?.error?.(res?.message || "Failed to update Timer Deals setting");
      }
    } catch (err) {
      setTimerShowOnHome(!nextValue);
      toast?.error?.("Failed to update Timer Deals setting");
    } finally {
      setTimerHomeToggleLoading(false);
    }
  };

  const handleToggleFlashShowOnHome = async () => {
    if (flashHomeToggleLoading) return;
    const nextValue = !flashShowOnHome;
    setFlashHomeToggleLoading(true);
    setFlashShowOnHome(nextValue);
    try {
      const res = await toggleFlashDiscountShowOnHome({ showOnHome: nextValue });
      if (res?.success) {
        toast?.success?.(
          nextValue
            ? "Flash Deals will now appear on Home"
            : "Flash Deals removed from Home"
        );
      } else {
        setFlashShowOnHome(!nextValue);
        toast?.error?.(res?.message || "Failed to update Flash Deals setting");
      }
    } catch (err) {
      setFlashShowOnHome(!nextValue);
      toast?.error?.("Failed to update Flash Deals setting");
    } finally {
      setFlashHomeToggleLoading(false);
    }
  };

  const handleToggleHideTimeline = async () => {
    if (timelineToggleLoading) return;
    const nextValue = !hideProductDeliveryTimeline;
    setTimelineToggleLoading(true);
    setHideProductDeliveryTimeline(nextValue);
    try {
      const res = await toggleHideProductDeliveryTimeline({ hideProductDeliveryTimeline: nextValue });
      if (res?.success) {
        toast?.success?.(
          nextValue
            ? "Delivery timeline hidden on customer Product Details"
            : "Delivery timeline shown on customer Product Details"
        );
      } else {
        setHideProductDeliveryTimeline(!nextValue);
        toast?.error?.(res?.message || "Failed to update timeline setting");
      }
    } catch (err) {
      setHideProductDeliveryTimeline(!nextValue);
      toast?.error?.("Failed to update timeline setting");
    } finally {
      setTimelineToggleLoading(false);
    }
  };

  const handleToggleHideStickyCategoryRow = async () => {
    if (stickyRowToggleLoading) return;
    const nextValue = !hideStickyCategoryRow;
    setStickyRowToggleLoading(true);
    setHideStickyCategoryRow(nextValue);
    if (nextValue) {
      setShowSubCategoriesInStickyCategory(false);
    }
    try {
      const res = await toggleHideStickyCategoryRow({ hideStickyCategoryRow: nextValue });
      if (res?.success) {
        if (nextValue && showSubCategoriesInStickyCategory) {
          await toggleShowSubCategoriesInStickyCategory({ showSubCategoriesInStickyCategory: false });
        }
        toast?.success?.(
          nextValue
            ? "Sticky category row hidden on customer Home"
            : "Sticky category row shown on customer Home"
        );
      } else {
        setHideStickyCategoryRow(!nextValue);
        toast?.error?.(res?.message || "Failed to update sticky category row setting");
      }
    } catch (err) {
      setHideStickyCategoryRow(!nextValue);
      toast?.error?.("Failed to update sticky category row setting");
    } finally {
      setStickyRowToggleLoading(false);
    }
  };

  const handleToggleShowSubCategoriesInStickyCategory = async () => {
    if (stickySubToggleLoading) return;
    const nextValue = !showSubCategoriesInStickyCategory;
    setStickySubToggleLoading(true);
    setShowSubCategoriesInStickyCategory(nextValue);
    try {
      const res = await toggleShowSubCategoriesInStickyCategory({ showSubCategoriesInStickyCategory: nextValue });
      if (res?.success) {
        toast?.success?.(
          nextValue
            ? "Sub-categories shown beside categories in sticky row"
            : "Sub-categories hidden in sticky row"
        );
      } else {
        setShowSubCategoriesInStickyCategory(!nextValue);
        toast?.error?.(res?.message || "Failed to update sticky sub-category setting");
      }
    } catch (err) {
      setShowSubCategoriesInStickyCategory(!nextValue);
      toast?.error?.("Failed to update sticky sub-category setting");
    } finally {
      setStickySubToggleLoading(false);
    }
  };

  const handleToggleShowSubCategoriesInMainCategory = async () => {
    if (mainSubToggleLoading) return;
    const nextValue = !showSubCategoriesInMainCategory;
    setMainSubToggleLoading(true);
    setShowSubCategoriesInMainCategory(nextValue);
    try {
      const res = await toggleShowSubCategoriesInMainCategory({ showSubCategoriesInMainCategory: nextValue });
      if (res?.success) {
        toast?.success?.(
          nextValue
            ? "Sub-categories shown beside categories in main section"
            : "Sub-categories hidden in main category section"
        );
      } else {
        setShowSubCategoriesInMainCategory(!nextValue);
        toast?.error?.(res?.message || "Failed to update main sub-category setting");
      }
    } catch (err) {
      setShowSubCategoriesInMainCategory(!nextValue);
      toast?.error?.("Failed to update main sub-category setting");
    } finally {
      setMainSubToggleLoading(false);
    }
  };

  const handleToggleHideMainCategory = async () => {
    if (hideMainCategoryToggleLoading) return;
    const nextValue = !hideMainCategory;
    setHideMainCategoryToggleLoading(true);
    setHideMainCategory(nextValue);
    if (nextValue) {
      setShowSubCategoriesInMainCategory(false);
    }
    try {
      const res = await toggleHideMainCategory({ hideMainCategory: nextValue });
      if (res?.success) {
        if (nextValue && showSubCategoriesInMainCategory) {
          await toggleShowSubCategoriesInMainCategory({ showSubCategoriesInMainCategory: false });
        }
        toast?.success?.(
          nextValue
            ? "Main category section hidden under banners on Home"
            : "Main category section shown on Home"
        );
      } else {
        setHideMainCategory(!nextValue);
        toast?.error?.(res?.message || "Failed to update main category visibility");
      }
    } catch (err) {
      setHideMainCategory(!nextValue);
      toast?.error?.("Failed to update main category visibility");
    } finally {
      setHideMainCategoryToggleLoading(false);
    }
  };

  const handleToggleGlobalCartLimit = async () => {
    if (globalCartLimitToggleLoading) return;
    const nextValue = !globalCartLimitEnabled;
    setGlobalCartLimitToggleLoading(true);
    setGlobalCartLimitEnabled(nextValue);
    try {
      const res = await toggleGlobalCartLimit({ enabled: nextValue });
      if (res?.success) {
        toast?.success?.(
          nextValue
            ? "Minimum cart value restriction enabled for customers"
            : "Minimum cart value restriction disabled"
        );
      } else {
        setGlobalCartLimitEnabled(!nextValue);
        toast?.error?.(res?.message || "Failed to update global cart limit setting");
      }
    } catch (err) {
      setGlobalCartLimitEnabled(!nextValue);
      toast?.error?.("Failed to update global cart limit setting");
    } finally {
      setGlobalCartLimitToggleLoading(false);
    }
  };

  const handleSaveGlobalMinimumCartValue = async () => {
    if (globalCartSaveLoading) return;
    const parsed = Number(globalMinimumCartValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast?.error?.("Please enter a valid minimum cart value greater than 0");
      return;
    }
    setGlobalCartSaveLoading(true);
    try {
      const res = await updateGlobalMinimumCartValue({ minimumCartValue: parsed });
      if (res?.success) {
        setSavedMinimumCartValue(parsed);
        toast?.success?.(`Minimum cart value set to ₹${parsed}`);
      } else {
        toast?.error?.(res?.message || "Failed to save minimum cart value");
      }
    } catch (err) {
      toast?.error?.("Failed to save minimum cart value");
    } finally {
      setGlobalCartSaveLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Control Centre</h1>
          <p className="text-sm text-gray-600 mt-2">
            Flash Deals behaviour on the customer app and optional delivery timeline visibility on Product Details.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
          {/* Flash Discount: Single Product Mode */}
          <div
            className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ${
              flashSingleProductMode
                ? "bg-gradient-to-r from-rose-50 to-pink-50 border-rose-300 shadow-sm"
                : "bg-white border-gray-200 hover:border-rose-200 hover:shadow-sm"
            }`}
            title="When ON, customers can add only one Flash Discount product at a time (they can still increase its quantity up to the per-order maximum)."
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                flashSingleProductMode ? "bg-rose-500 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              <FaBolt className="text-base" />
            </span>
            <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
              <span className="text-sm font-semibold text-gray-800">Flash Discount · Single Product</span>
              <span className="text-[11px] text-gray-500">
                {flashSingleProductMode
                  ? "Only one flash item per cart"
                  : "Multiple flash items allowed"}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={flashSingleProductMode}
              disabled={flashToggleLoading || initialLoading}
              onClick={handleToggleFlashSingleProductMode}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-400 ${
                flashSingleProductMode ? "bg-rose-500" : "bg-gray-300"
              } ${flashToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                  flashSingleProductMode ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Flash Deals on Home */}
          <div
            className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ${
              flashShowOnHome
                ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 shadow-sm"
                : "bg-white border-gray-200 hover:border-amber-200 hover:shadow-sm"
            }`}
            title="When ON, the Flash Deals strip is shown on the customer Home screen, right under the Trust Badge bar."
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                flashShowOnHome ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              <FaHome className="text-base" />
            </span>
            <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
              <span className="text-sm font-semibold text-gray-800">Add Flash Deals in Home</span>
              <span className="text-[11px] text-gray-500">
                {flashShowOnHome ? "Flash strip visible on Home" : "Flash strip hidden on Home"}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={flashShowOnHome}
              disabled={flashHomeToggleLoading || initialLoading}
              onClick={handleToggleFlashShowOnHome}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 ${
                flashShowOnHome ? "bg-amber-500" : "bg-gray-300"
              } ${flashHomeToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                  flashShowOnHome ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Timer Deals on Home */}
          <div
            className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ${
              timerShowOnHome
                ? "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300 shadow-sm"
                : "bg-white border-gray-200 hover:border-yellow-200 hover:shadow-sm"
            }`}
            title="When ON, Timer Deals strips appear on customer Home below Flash Deals."
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                timerShowOnHome ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              <FaClock className="text-base" />
            </span>
            <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
              <span className="text-sm font-semibold text-gray-800">Add Timer Deals in Home</span>
              <span className="text-[11px] text-gray-500">
                {timerShowOnHome ? "Timer strips visible on Home" : "Timer strips hidden on Home"}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={timerShowOnHome}
              disabled={timerHomeToggleLoading || initialLoading}
              onClick={handleToggleTimerShowOnHome}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 ${
                timerShowOnHome ? "bg-yellow-500" : "bg-gray-300"
              } ${timerHomeToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                  timerShowOnHome ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Hide timeline on app Product Details */}
          <div
            className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ${
              hideProductDeliveryTimeline
                ? "bg-gradient-to-r from-slate-50 to-gray-100 border-slate-300 shadow-sm"
                : "bg-white border-gray-200 hover:border-slate-200 hover:shadow-sm"
            }`}
            title="When ON, the Pre-order / Delivery timeline block is hidden on the customer mobile Product Details screen."
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                hideProductDeliveryTimeline ? "bg-slate-700 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              <MdTimeline className="text-xl" />
            </span>
            <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
              <span className="text-sm font-semibold text-gray-800">Hide Timeline</span>
              <span className="text-[11px] text-gray-500">
                {hideProductDeliveryTimeline
                  ? "Timeline hidden on Product Details in the app"
                  : "Timeline shown when delivery info is available"}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={hideProductDeliveryTimeline}
              disabled={timelineToggleLoading || initialLoading}
              onClick={handleToggleHideTimeline}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 ${
                hideProductDeliveryTimeline ? "bg-slate-700" : "bg-gray-300"
              } ${timelineToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                  hideProductDeliveryTimeline ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Hide sticky category row on customer Home */}
          <div
            className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ${
              hideStickyCategoryRow
                ? "bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-300 shadow-sm"
                : "bg-white border-gray-200 hover:border-indigo-200 hover:shadow-sm"
            }`}
            title="When ON, the compact category strip below the search bar is hidden on the customer mobile Home screen."
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                hideStickyCategoryRow ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              <FaThList className="text-base" />
            </span>
            <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
              <span className="text-sm font-semibold text-gray-800">Hide Sticky Category Row</span>
              <span className="text-[11px] text-gray-500">
                {hideStickyCategoryRow
                  ? "Category strip hidden on Home in the app"
                  : "Category strip visible below search on Home"}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={hideStickyCategoryRow}
              disabled={stickyRowToggleLoading || initialLoading}
              onClick={handleToggleHideStickyCategoryRow}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 ${
                hideStickyCategoryRow ? "bg-indigo-600" : "bg-gray-300"
              } ${stickyRowToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                  hideStickyCategoryRow ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {!hideStickyCategoryRow && (
            <div
              className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ml-4 ${
                showSubCategoriesInStickyCategory
                  ? "bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-300 shadow-sm"
                  : "bg-white border-gray-200 hover:border-violet-200 hover:shadow-sm"
              }`}
              title="When ON, sub-categories appear beside categories in the sticky row on Home."
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  showSubCategoriesInStickyCategory ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-500"
                }`}
              >
                <FaLayerGroup className="text-base" />
              </span>
              <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
                <span className="text-sm font-semibold text-gray-800">
                  Show SubCategories besides Categories in Sticky Category
                </span>
                <span className="text-[11px] text-gray-500">
                  {showSubCategoriesInStickyCategory
                    ? "Sub-categories shown in sticky row"
                    : "Only parent categories in sticky row"}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showSubCategoriesInStickyCategory}
                disabled={stickySubToggleLoading || initialLoading}
                onClick={handleToggleShowSubCategoriesInStickyCategory}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-400 ${
                  showSubCategoriesInStickyCategory ? "bg-violet-600" : "bg-gray-300"
                } ${stickySubToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                    showSubCategoriesInStickyCategory ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          )}

          <div
            className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ${
              hideMainCategory
                ? "bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-300 shadow-sm"
                : "bg-white border-gray-200 hover:border-indigo-200 hover:shadow-sm"
            }`}
            title="When ON, the Shop by Category block under banners is hidden on the customer mobile Home screen."
          >
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                hideMainCategory ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              <MdCategory className="text-xl" />
            </span>
            <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
              <span className="text-sm font-semibold text-gray-800">Hide Main Category</span>
              <span className="text-[11px] text-gray-500">
                {hideMainCategory
                  ? "Main category section hidden under banners"
                  : "Main category section visible on Home"}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={hideMainCategory}
              disabled={hideMainCategoryToggleLoading || initialLoading}
              onClick={handleToggleHideMainCategory}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 ${
                hideMainCategory ? "bg-indigo-600" : "bg-gray-300"
              } ${hideMainCategoryToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                  hideMainCategory ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {!hideMainCategory && (
            <div
              className={`group flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 ml-4 ${
                showSubCategoriesInMainCategory
                  ? "bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-300 shadow-sm"
                  : "bg-white border-gray-200 hover:border-violet-200 hover:shadow-sm"
              }`}
              title="When ON, sub-categories appear beside categories in the main Shop by Category section."
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  showSubCategoriesInMainCategory ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-500"
                }`}
              >
                <FaLayerGroup className="text-base" />
              </span>
              <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
                <span className="text-sm font-semibold text-gray-800">
                  Show SubCategories besides Categories In Main Category
                </span>
                <span className="text-[11px] text-gray-500">
                  {showSubCategoriesInMainCategory
                    ? "Sub-categories shown beside categories in main section"
                    : "Only parent categories in main section"}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showSubCategoriesInMainCategory}
                disabled={mainSubToggleLoading || initialLoading}
                onClick={handleToggleShowSubCategoriesInMainCategory}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-400 ${
                  showSubCategoriesInMainCategory ? "bg-violet-600" : "bg-gray-300"
                } ${mainSubToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                    showSubCategoriesInMainCategory ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          )}

          {/* Global Minimum Cart Value */}
          <div
            className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
              globalCartLimitEnabled
                ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300 shadow-sm"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className="group flex flex-wrap items-center gap-3 px-4 py-3"
              title="When ON, customers must reach the minimum cart value before they can proceed to checkout on web and mobile."
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  globalCartLimitEnabled ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-500"
                }`}
              >
                <FaShoppingCart className="text-base" />
              </span>
              <div className="flex-1 min-w-[200px] flex flex-col leading-tight">
                <span className="text-sm font-semibold text-gray-800">Global Minimum Cart Value</span>
                <span className="text-[11px] text-gray-500">
                  {globalCartLimitEnabled
                    ? `Customers need at least ₹${savedMinimumCartValue || 0} in cart to checkout`
                    : "No minimum cart restriction for customers"}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={globalCartLimitEnabled}
                disabled={globalCartLimitToggleLoading || initialLoading}
                onClick={handleToggleGlobalCartLimit}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-400 ${
                  globalCartLimitEnabled ? "bg-emerald-600" : "bg-gray-300"
                } ${globalCartLimitToggleLoading || initialLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                    globalCartLimitEnabled ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            {globalCartLimitEnabled && (
              <div className="px-4 pb-4 pt-1 border-t border-emerald-200/60">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Minimum cart value (₹)
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={globalMinimumCartValue}
                    onChange={(e) => setGlobalMinimumCartValue(e.target.value)}
                    placeholder="e.g. 299"
                    className="flex-1 min-w-[140px] rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                  <button
                    type="button"
                    onClick={handleSaveGlobalMinimumCartValue}
                    disabled={globalCartSaveLoading || initialLoading}
                    className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {globalCartSaveLoading ? "Saving..." : "Save"}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-gray-500">
                  Shown on customer cart (web &amp; app). Proceed is blocked until cart value meets this minimum.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlCentre;
